import Dialog from '@mui/material/Dialog';
import { DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField, Button, Grid, Box, InputAdornment } from '@mui/material';
import { EditVendorModal, defaultEditVendorModal } from 'src/types/EditVendorModal';
import { useState, useEffect, useCallback } from 'react';
import { ApplicationRole } from 'src/types/applicationRoll';
import { get } from 'src/api/Api';
import { GenerateRandomPassword } from 'src/utils/helperFunctions';

interface VendorEditFormProps {
  cancelEdit: () => void;
  onSubmit: (editVendorModal: EditVendorModal) => void;
  isOpen: boolean;
  user: EditVendorModal;
}

const VendorEditForm = (props: VendorEditFormProps) => {
  const [state, setState] = useState<EditVendorModal>({ ...defaultEditVendorModal });
  const [roles, setRoles] = useState<string[]>([]);

  const [usernameError, setUsernameError] = useState(false);
  const [showPasswordError, setShowPasswordError] = useState(false);

  useEffect(() => {
    const init = async () => {
      let r = await get<ApplicationRole[]>('/UserAdmin/vendorPortalRoles');
      setRoles(r.map((x) => x.roleName));

      if (props.user.isNewUser) {
        let newPass = GenerateRandomPassword(12);
        setState({ ...props.user, newPassword: newPass });
      }

      setState({ ...props.user });
      setUsernameError(false);
      setShowPasswordError(false);
    };
    init();
  }, [props.user]);

  const passwordValid = useCallback(() => {
    if (state.newPassword !== '') {
      //check that state.newPassword is greather than 9 characters and has at least one number and one special character
      if (state.newPassword.length < 9 || !state.newPassword.match(/^(?=.*\d)(?=.*[!@#$%^&*])(.{10,})$/)) {
        return false;
      }
    }

    return true;
  }, [state.newPassword]);

  useEffect(() => {
    setShowPasswordError(!passwordValid());
  }, [state.newPassword, passwordValid]);

  const onFormChange = (e) => {
    setState({ ...state, [e.target.name]: e.target.value });
  };

  const onPasswordChange = (e) => {
    passwordValid();
    setState({ ...state, [e.target.name]: e.target.value });
  };

  const allowSubmit = (): boolean => {
    return (
      !showPasswordError &&
      !usernameError &&
      passwordValid() &&
      state.username !== '' &&
      state.role !== '' &&
      state.email !== '' &&
      state.role !== '' &&
      (state.newPassword !== '' || state.companyUserId !== 0)
    );
  };

  const cancelWithoutSubmit = (e) => {
    e.preventDefault();
    setState({ ...defaultEditVendorModal });
    props.cancelEdit();
  };

  const checkUsernameAvailable = async () => {
    //only run on add user path
    if (state.username !== '' && state.companyUserId === 0) {
      let usernameExists: boolean = await get<boolean>('/UserAdmin/DoesUsernameExist?username=' + encodeURIComponent(state.username));
      setUsernameError(usernameExists);
    }
  };

  return (
    <Dialog maxWidth={'xs'} fullWidth open={props.isOpen} onClose={props.cancelEdit}>
      <DialogTitle style={{ height: '10px' }} id="edit-user-dialog">
        {state.companyUserId == 0 ? 'Add User' : 'Edit User'}
      </DialogTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setState({ ...defaultEditVendorModal });
          props.onSubmit(state);
        }}
      >
        <div className="px-2 pt-2">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <div className="pt-1">
                <TextField
                  name="username"
                  label="User Name"
                  type="text"
                  InputLabelProps={{ shrink: true }}
                  value={state.username}
                  onChange={onFormChange}
                  onBlur={checkUsernameAvailable}
                  fullWidth
                />
                {usernameError && <span style={{ margin: '0 0 0 0', padding: '0px 0px 0px 10px', fontSize: 'small', color: 'red' }}>Username already exists</span>}
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className="pt-1">
                <TextField name="email" label="Email" type="text" InputLabelProps={{ shrink: true }} value={state.email} onChange={onFormChange} fullWidth />
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className="pt-1">
                <FormControl size="small" fullWidth>
                  <InputLabel shrink={true} id="role-input">
                    Role
                  </InputLabel>
                  <Select name="role" label="Role" notched={true} value={state.role} onChange={onFormChange}>
                    {roles.map((r, i) => {
                      return (
                        <MenuItem key={i} value={r}>
                          {r}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className="pt-1">
                <FormControl size="small" fullWidth>
                  <InputLabel shrink={true} id="isactive-input">
                    Is Active
                  </InputLabel>
                  <Select name="isActive" label="Is Active" notched={true} value={state.isActive.toString()} onChange={onFormChange}>
                    <MenuItem value={'true'}>True</MenuItem>
                    <MenuItem value={'false'}>False</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </Grid>
            <Grid item xs={12}>
              <div style={{ marginBottom: '10px' }} className="pt-1">
                <TextField
                  name="newPassword"
                  fullWidth
                  label="New Password"
                  type="text"
                  InputLabelProps={{ shrink: true }}
                  value={state.newPassword}
                  onChange={onPasswordChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" onClick={() => setState({ ...state, newPassword: GenerateRandomPassword(12) })} variant="outlined">
                          Generate Password
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              {showPasswordError && (
                <ul style={{ margin: '0 0 0 0', padding: '0px 0px 0px 10px', fontSize: 'small', color: 'red' }}>
                  <li> At least 10 characters</li>
                  <li> At least 1 special character</li>
                  <li> At least 1 number</li>
                </ul>
              )}
            </Grid>
          </Grid>
          <Box sx={{ pt: 1, pb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" type={'submit'} disabled={!allowSubmit()}>
              Save User
            </Button>
            <Button style={{ marginLeft: '15px' }} onClick={cancelWithoutSubmit} variant="contained">
              Cancel
            </Button>
          </Box>
        </div>
      </form>
    </Dialog>
  );
};

export default VendorEditForm;
